import type { CSSProperties } from 'react'

export type ImagePlaceholderProps = {
  /**
   * When set, the first letter is rendered as a monogram. Omit (or pass an
   * empty string) for a neutral product glyph. Pass the item name at product /
   * rental / cart tiles so an imageless card reads as intentional, not broken.
   */
  label?: string | null
  /**
   * Sizing / positioning classes for the root box — the caller owns the shape.
   * e.g. "absolute inset-0" (fill a `relative` parent), "w-full h-full",
   * "w-full aspect-[4/3]".
   */
  className?: string
}

/**
 * Branded fallback for imageless product / rental / cart tiles.
 *
 * Pure CSS + inline SVG — no `<img>`, no `next/image`, no network fetch. A
 * per-card logo fetch would defeat the point on a grid of imageless SKUs
 * (design brief F11: "zero per-card network fetches"). Instead it picks up the
 * tenant's identity for free: the glyph / monogram is tinted with
 * `--brand-primary` at low opacity over `--color-surface-muted`, so the tile
 * reads as "intentionally on-brand" rather than "broken image".
 *
 * Replaces the ad-hoc empty gray boxes + the literal no-image text that were
 * scattered across ProductGrid, UpsellRow, CartDrawer and the rentals list.
 *
 * Decorative by default (`aria-hidden`) — every call site shows the item name
 * as adjacent text, so the placeholder carries no information of its own.
 */
export function ImagePlaceholder({ label, className }: ImagePlaceholderProps) {
  const initial = label?.trim().charAt(0).toUpperCase() || null
  // --brand-primary may already carry the dealer's alpha; fillOpacity keeps the
  // mark subtle either way. Neutral slate fallback for a brand-less tenant.
  const tint: CSSProperties = { color: 'var(--brand-primary, #94a3b8)' }
  const glyph = 'w-2/5 max-w-[96px] aspect-square'

  return (
    <div
      aria-hidden="true"
      data-component="ImagePlaceholder"
      className={`flex items-center justify-center overflow-hidden bg-[var(--color-surface-muted,#f3f4f6)] ${className ?? ''}`}
      style={tint}
    >
      {initial ? (
        <svg viewBox="0 0 48 48" className={glyph} role="presentation">
          <text
            x="24"
            y="25"
            textAnchor="middle"
            dominantBaseline="central"
            fill="currentColor"
            fillOpacity="0.28"
            fontSize="34"
            fontWeight="700"
            style={{ fontFamily: 'var(--font-heading, inherit)' }}
          >
            {initial}
          </text>
        </svg>
      ) : (
        <svg viewBox="0 0 48 48" fill="none" className={glyph} role="presentation">
          {/* Calm wave / foil glyph — evokes the product without a logo fetch. */}
          <path
            d="M4 30c5 0 5-4 10-4s5 4 10 4 5-4 10-4 5 4 10 4"
            stroke="currentColor"
            strokeOpacity="0.3"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 20c5 0 5-4 10-4s5 4 10 4 5-4 10-4 5 4 10 4"
            stroke="currentColor"
            strokeOpacity="0.18"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  )
}
